<template>
  <AuthLayout>
    <template #eyebrow>ScrapingAnt</template>
    <template #headline>Scrape the web without getting blocked.</template>
    <template #subhead>
      Send a request, get structured data back. We handle proxies, headless
      browsers, and anti-bot bypass so you don't have to.
    </template>

    <template #features>
      <li class="auth-feature">
        <img src="/auth/icon-bypass.svg" alt="" aria-hidden="true" />
        <div>
          <p class="auth-feature__title">Bypass anti-bot systems</p>
          <p class="auth-feature__body">
            Cloudflare, DataDome, PerimeterX handled automatically.
          </p>
        </div>
      </li>
      <li class="auth-feature">
        <img src="/auth/icon-proxies.svg" alt="" aria-hidden="true" />
        <div>
          <p class="auth-feature__title">Residential + datacenter proxies</p>
          <p class="auth-feature__body">
            190+ countries, auto-rotated per request.
          </p>
        </div>
      </li>
      <li class="auth-feature">
        <img src="/auth/icon-js.svg" alt="" aria-hidden="true" />
        <div>
          <p class="auth-feature__title">Headless Chrome on tap</p>
          <p class="auth-feature__body">
            Render JavaScript, run custom JS, screenshot any page.
          </p>
        </div>
      </li>
      <li class="auth-feature">
        <img src="/auth/icon-scale.svg" alt="" aria-hidden="true" />
        <div>
          <p class="auth-feature__title">Scale with your project</p>
          <p class="auth-feature__body">
            Concurrency, IP whitelisting, MCP server, Python/JS/PHP/Java/C# SDKs.
          </p>
        </div>
      </li>
    </template>

    <template #footnote>
      Trusted by developers at startups and Fortune&nbsp;500 teams · No credit
      card required
    </template>

    <template #mobileBanner>
      <p class="auth-mobile-banner__eyebrow">ScrapingAnt</p>
      <p class="auth-mobile-banner__headline">
        Scrape the web without getting blocked.
      </p>
    </template>

    <template #form>
      <v-card class="auth-card elevation-2">
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"
        />

        <div class="auth-card__head">
          <h2 v-if="!signup_success" class="auth-card__title">
            Create your free account
          </h2>
          <p v-if="!signup_success" class="auth-card__subtitle">
            10,000 free API credits every month. No credit card required.
          </p>
        </div>

        <SocialLoginButtons
          v-if="!signup_success"
          class="auth-card__social"
        />

        <div v-if="!signup_success" class="auth-card__divider">
          <span>or sign up with email</span>
        </div>

        <div v-if="signup_success" class="auth-card__head">
          <h2 class="auth-card__title">You're all set</h2>
          <p class="auth-card__subtitle">
            Please check your email
            <b>{{ form.body.email }}</b> for the confirmation letter.
          </p>
          <p class="auth-card__subtitle" style="margin-top: 12px">
            <b>Didn't receive the email?</b> Check your spam folder or click
            <b>Resend email</b> below.
          </p>
        </div>

        <div v-if="signup_success" class="auth-card__actions">
          <v-btn text :to="{ name: 'Login' }">Log in</v-btn>
          <v-btn
            color="primary"
            :disabled="confirmation_resent"
            @click="resend"
          >Resend email</v-btn>
        </div>

        <div
          v-if="confirmation_resent"
          class="confirmation-message"
          style="margin-top: 20px"
        >
          <div>
            Confirmation email has been resent. Please contact us at
            <a href="mailto:support@scrapingant.com">support@scrapingant.com</a>
            if the email was not received.
          </div>
        </div>

        <div
          v-if="!signup_success"
          class="auth-card__form"
          v-on:keyup.enter="registerOnEnter"
        >
          <v-text-field
            v-model="$v.form.body.email.$model"
            :error-messages="emailErrors"
            @input="$v.form.body.email.$touch()"
            @blur="$v.form.body.email.$touch()"
            autofocus
            required
            label="E-mail"
            class="mb-2"
          ></v-text-field>
          <v-text-field
            v-model="$v.form.body.password.$model"
            :type="showNewPassword ? 'text' : 'password'"
            :error-messages="newPasswordErrors"
            :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @input="$v.form.body.password.$touch()"
            @click:append="showNewPassword = !showNewPassword"
            label="Password"
            class="mb-2"
          ></v-text-field>
          <v-text-field
            v-model="$v.form.body.repeated_password.$model"
            :type="showPasswordOneMoreTime ? 'text' : 'password'"
            :error-messages="OneMoreTimePasswordErrors"
            :append-icon="showPasswordOneMoreTime ? 'mdi-eye' : 'mdi-eye-off'"
            @input="$v.change_password.new_password_repeated.$touch()"
            @blur="$v.change_password.new_password_repeated.$touch()"
            @click:append="showPasswordOneMoreTime = !showPasswordOneMoreTime"
            label="Confirm password"
          ></v-text-field>

          <v-checkbox
            v-model="acceptedTOS"
            class="auth-card__tos"
            :rules="[(v) => !!v || 'You must accept the terms']"
          >
            <template v-slot:label>
              <span class="auth-card__tos-label"
                >I accept the
                <a
                  href="https://scrapingant.com/legal/terms-of-use/"
                  @click.stop
                  target="_blank"
                  >Terms of Use</a
                >
                and
                <a
                  href="https://scrapingant.com/legal/privacy-policy/"
                  @click.stop
                  target="_blank"
                  >Privacy Policy</a
                >
              </span>
            </template>
          </v-checkbox>

          <vue-recaptcha
            :sitekey="constants.RECAPTCHA_SITE_KEY"
            @verify="captcha_verify"
          ></vue-recaptcha>

          <span class="form-error">{{ form.error }}</span>
        </div>

        <div v-if="!signup_success" class="auth-card__cta">
          <v-btn
            color="primary"
            block
            large
            :disabled="isDisabledForm || !acceptedTOS"
            @click="register"
          >Create free account</v-btn>
          <p class="auth-card__trust">
            <v-icon small>mdi-lock-outline</v-icon>
            We never share your email.
          </p>
        </div>

        <div v-if="!signup_success" class="auth-card__switch">
          Already have an account?
          <router-link :to="{ name: 'Login' }">Log in</router-link>
        </div>

        <div class="loading-overlay" v-if="loading">
          <v-progress-circular
            :size="50"
            :width="2"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </v-card>
    </template>
  </AuthLayout>
</template>

<script>
import { required, email, minLength, sameAs } from "vuelidate/lib/validators";
import constants from "@/config/constants";
import SocialLoginButtons from "@/views/SocialLoginButtons";
import AuthLayout from "@/components/auth/AuthLayout";
import { VueRecaptcha } from "vue-recaptcha";
import { getCookie, getFirstTouch, firstTouchParams } from "@/utils/common_utils";

export default {
  name: "Signup",
  components: { SocialLoginButtons, VueRecaptcha, AuthLayout },
  data() {
    return {
      constants,
      loading: false,
      signup_success: false,
      confirmation_resent: false,
      captcha_response: null,
      showPasswordOneMoreTime: false,
      showNewPassword: false,
      acceptedTOS: false,
      form: {
        body: {
          email: "",
          password: "",
          repeated_password: "",
        },
        error: "",
      },
    };
  },
  validations: {
    form: {
      body: {
        email: {
          required,
          email,
        },
        password: {
          required,
          minLength: minLength(6),
        },
        repeated_password: {
          required,
          sameAsPassword: sameAs("password"),
        },
      },
    },
  },
  computed: {
    OneMoreTimePasswordErrors() {
      const errors = [];
      if (!this.$v.form.body.repeated_password.$dirty) return errors;
      !this.$v.form.body.repeated_password.required &&
        errors.push("Passwords do not match");
      !this.$v.form.body.repeated_password.sameAsPassword &&
        errors.push("Passwords do not match");
      return errors;
    },
    newPasswordErrors() {
      const errors = [];
      if (!this.$v.form.body.password.$dirty) return errors;
      !this.$v.form.body.password.required &&
        errors.push("Please enter a password with 6+ characters");
      !this.$v.form.body.password.minLength &&
        errors.push("Please enter a password with 6+ characters");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.form.body.email.$dirty) return errors;
      !this.$v.form.body.email.email &&
        errors.push("Please enter a valid email");
      !this.$v.form.body.email.required &&
        errors.push("Please enter a valid email");
      return errors;
    },
    isDisabledForm() {
      return (
        this.$v.form.body.email.$error ||
        this.$v.form.body.password.$error ||
        this.$v.form.body.repeated_password.$error ||
        !this.$v.form.body.email.$dirty ||
        !this.$v.form.body.repeated_password.$dirty ||
        !this.$v.form.body.password.$dirty ||
        !this.captcha_response
      );
    },
  },
  methods: {
    errors(res) {
      this.form.error =
        res.data?.error?.msg ?? "Error occurred. Please, try again.";
    },

    register() {
      this.$gtag.event(constants.ANALYTICS_EVENTS.SIGN_UP, firstTouchParams());
      const userSource = getCookie("user_source");
      this.loading = true;
      this.$store
        .dispatch("auth/register", {
          ...this.form.body,
          user_source: userSource ? userSource : "direct",
          first_touch: getFirstTouch(),
          "recaptcha-token": this.captcha_response,
        })
        .then(
          // eslint-disable-next-line no-unused-vars
          (success) => {
            this.loading = false;
            this.signup_success = true;
          },
          (res) => {
            this.loading = false;
            this.errors(res.response);
          }
        );
    },
    captcha_verify(response) {
      this.captcha_response = response;
    },
    registerOnEnter() {
      if (!this.isDisabledForm) {
        this.register();
      }
    },

    resend() {
      this.$gtag.event(constants.ANALYTICS_EVENTS.RESEND_EMAIL, {
        event_category: constants.ANALYTICS_EVENTS.DEFAULT_CATEGORY,
      });
      this.loading = true;
      this.$store.dispatch("auth/resend_email", this.form.body.email).then(
        // eslint-disable-next-line no-unused-vars
        (success) => {
          this.loading = false;
          this.confirmation_resent = true;
        },
        (res) => {
          this.loading = false;
          this.errors(res.response);
        }
      );
    },
  },
};
</script>

<style scoped lang="scss">
.auth-card {
  position: relative;
  width: 100%;
  max-width: 440px;
  padding: 40px 36px 32px;
  border-radius: 12px;
}

.auth-card__head {
  text-align: center;
  margin-bottom: 24px;
}

.auth-card__title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
  color: #0f172a;
  margin: 0 0 8px;
}

.auth-card__subtitle {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(15, 23, 42, 0.68);
  margin: 0;
}

.auth-card__social {
  margin-bottom: 20px;
}

.auth-card__divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0 16px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(15, 23, 42, 0.12);
  }
}

.auth-card__form {
  display: flex;
  flex-direction: column;
}

.auth-card__tos {
  margin-top: 0;
}

.auth-card__tos-label {
  font-size: 13px;

  a {
    color: #e2782f;
    text-decoration: underline;
  }
}

.auth-card__cta {
  margin-top: 20px;
}

.auth-card__trust {
  margin: 12px 0 0;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.55);
  text-align: center;

  .v-icon {
    margin-right: 4px;
    color: rgba(15, 23, 42, 0.55);
  }
}

.auth-card__switch {
  margin-top: 20px;
  text-align: center;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.7);

  a {
    color: #e2782f;
    font-weight: 500;
    text-decoration: none;
    margin-left: 4px;
  }
}

.auth-card__actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.form-error {
  color: #d32f2f;
  font-size: 13px;
  margin-top: 8px;
  display: block;
  min-height: 18px;
}
</style>
